'use client';
import { Button } from '@skin-seoul/design-system';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { Suspense, type ReactElement, type ReactNode } from 'react';
import { ErrorBoundary } from './ErrorBoundary';
import { ClientOnly } from './ClientOnly';

interface QueryBoundaryProps {
  children: ReactNode;
  clientOnly?: boolean;
  loadingFallback?: ReactElement;
  errorFallback?: ReactElement;
}

export const DefaultErrorFallback = ({ resetBoundary }: { resetBoundary?: () => void }) => {
  return (
    <div className="flex flex-col items-center gap-4">
      <p className="text-body2 font-bold">Something went wrong</p>
      <Button variant="primary" onClick={resetBoundary}>
        Retry
      </Button>
    </div>
  );
};

const QueryBoundary = ({ children, errorFallback, loadingFallback, clientOnly = false }: QueryBoundaryProps) => {
  const { reset } = useQueryErrorResetBoundary();

  if (clientOnly) {
    return (
      <ClientOnly fallback={loadingFallback || <></>}>
        <ErrorBoundary fallback={errorFallback || <DefaultErrorFallback />} resetQuery={reset}>
          <Suspense fallback={loadingFallback || <></>}>{children}</Suspense>
        </ErrorBoundary>
      </ClientOnly>
    );
  }

  return (
    <ErrorBoundary fallback={errorFallback || <DefaultErrorFallback />} resetQuery={reset}>
      <Suspense fallback={loadingFallback || <></>}>{children}</Suspense>
    </ErrorBoundary>
  );
};

export default QueryBoundary;
